
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useAdmin } from '@/hooks/useAdmin';
import { toast } from 'sonner';

interface SecurityContextType {
  isSecurityActive: boolean;
  toggleDeveloperTools: (enabled: boolean) => void;
}

const SecurityContext = createContext<SecurityContextType>({
  isSecurityActive: true,
  toggleDeveloperTools: () => {},
});

export const useSecurityContext = () => useContext(SecurityContext);

interface SecurityProviderProps {
  children: React.ReactNode;
}

export const SecurityProvider: React.FC<SecurityProviderProps> = ({ children }) => {
  const { isAdmin } = useAdmin();
  const [isDeveloperToolsEnabled, setIsDeveloperToolsEnabled] = useState(false);
  const [isProduction, setIsProduction] = useState(false);
  const [violations, setViolations] = useState(0);

  // Check if we're in production
  useEffect(() => {
    const isProd = !window.location.hostname.includes('localhost') && 
                  !window.location.hostname.includes('127.0.0.1') &&
                  !window.location.hostname.includes('lovable.app');
    
    console.log('Environment detection:', { 
      hostname: window.location.hostname, 
      isProd,
      isAdmin
    });
    
    setIsProduction(isProd);
  }, [isAdmin]);

  // Basic F12 key blocking for the entire site
  useEffect(() => {
    // Only block F12 if in production and not an admin with dev tools enabled
    if (!isProduction || (isAdmin && isDeveloperToolsEnabled)) {
      return;
    }

    const handleF12 = (e: KeyboardEvent) => {
      if (e.key === 'F12' || e.keyCode === 123) {
        e.preventDefault();
        e.stopPropagation();
        
        setViolations(prev => {
          const newCount = prev + 1;
          
          if (newCount === 1) {
            toast.warning("Developer tools access is restricted", {
              description: "This action has been logged for security purposes.",
              duration: 5000,
            });
          }
          
          return newCount;
        });
        
        return false;
      }
    };
    
    // Add event listeners with capture phase to intercept early
    window.addEventListener('keydown', handleF12, true);
    document.addEventListener('keydown', handleF12, true);
    
    return () => {
      window.removeEventListener('keydown', handleF12, true);
      document.removeEventListener('keydown', handleF12, true);
    };
  }, [isProduction, isAdmin, isDeveloperToolsEnabled]);

  const toggleDeveloperTools = (enabled: boolean) => {
    setIsDeveloperToolsEnabled(enabled);
    setViolations(0); // Reset violation count
    
    if (enabled) {
      toast.success("Developer tools access enabled for admin");
    } else {
      toast.info("Developer tools access disabled");
    }
  };

  return (
    <SecurityContext.Provider 
      value={{ 
        isSecurityActive: isProduction, 
        toggleDeveloperTools 
      }}
    >
      {children}
    </SecurityContext.Provider>
  );
};
